// src/pages/Settings.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FullBleed from '../components/FullBleed';
import StageFrame from '../components/StageFrame';
import bg from '../assets/card.png';
import { DESIGN_W, DESIGN_H, MAX_PC_WIDTH } from '../components/card/layout';

axios.defaults.withCredentials = true;

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ name: '', email: '', birthday: '', carrier: '' });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/member/profile');
        setForm({
          name: data?.NAME || '',
          email: data?.EMAIL || '',
          birthday: data?.BIRTHDAY || '',
          carrier: data?.CARRIER || '',
        });
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setMsg('');
  };

  const handleSave = async () => {
    const carrierVal = form.carrier.trim().toUpperCase();
    if (carrierVal && !/^\/[A-Z0-9+.\-]{7}$/.test(carrierVal)) {
      setMsg('載具格式錯誤，應為 / 開頭加 7 碼英數字（例如 /ABC1234）');
      return;
    }

    setSaving(true);
    setMsg('');
    try {
      await axios.post('/api/member/update', {
        name: form.name.trim(),
        email: form.email.trim(),
        birthday: form.birthday,
        carrier: carrierVal,
      });
      setMsg('儲存成功！');
    } catch (err) {
      setMsg(err.response?.data?.error || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={DESIGN_W || 1080}
        designH={DESIGN_H || 1920}
        maxPcWidth={MAX_PC_WIDTH || 1080}
        overlay={({ px }) => (
          <>
            <style>{`
              .st-container {
                position: absolute;
                top: ${px(120)}px;
                left: ${px(80)}px;
                right: ${px(80)}px;
                bottom: ${px(180)}px;
                overflow-y: auto;
                -webkit-overflow-scrolling: touch;
              }
              .st-title {
                font-size: ${px(58)}px;
                font-weight: 900;
                color: #5e3a1a;
                text-align: center;
                margin: 0 0 ${px(50)}px;
                letter-spacing: ${px(6)}px;
              }
              .st-card {
                background: rgba(240,223,212,.92);
                border-radius: ${px(24)}px;
                padding: ${px(44)}px ${px(40)}px;
                box-shadow: 0 ${px(4)}px ${px(16)}px rgba(94,58,26,.1);
                border: ${px(2)}px solid rgba(194,139,78,.3);
              }
              .st-field {
                margin-bottom: ${px(44)}px;
              }
              .st-field:last-child {
                margin-bottom: 0;
              }
              .st-label {
                display: block;
                font-size: ${px(42)}px;
                font-weight: 900;
                color: #5e3a1a;
                margin-bottom: ${px(16)}px;
                letter-spacing: ${px(3)}px;
              }
              .st-input {
                width: 100%;
                padding: ${px(30)}px ${px(28)}px;
                font-size: ${px(38)}px;
                border: ${px(3)}px solid #c28b4e;
                border-radius: ${px(14)}px;
                background: rgba(255,255,255,.85);
                box-sizing: border-box;
                color: #333;
                outline: none;
                transition: border-color .2s;
              }
              .st-input:focus {
                border-color: #5e3a1a;
              }
              .st-hint {
                font-size: ${px(32)}px;
                color: #8b7355;
                margin-top: ${px(12)}px;
              }
              .st-msg {
                text-align: center;
                font-size: ${px(36)}px;
                padding: ${px(20)}px;
                border-radius: ${px(12)}px;
                margin-top: ${px(28)}px;
                font-weight: 700;
              }
              .st-msg-ok {
                background: rgba(194,139,78,.15);
                color: #5e3a1a;
                border: ${px(2)}px solid #c28b4e;
              }
              .st-msg-err {
                background: rgba(200,0,0,.08);
                color: #c00;
                border: ${px(2)}px solid #c00;
              }
              .st-save-btn {
                display: block;
                width: 100%;
                box-sizing: border-box;
                margin-top: ${px(40)}px;
                padding: ${px(30)}px;
                font-size: ${px(42)}px;
                font-weight: 900;
                border: none;
                border-radius: ${px(16)}px;
                background: #5e3a1a;
                color: #f0dfd4;
                cursor: pointer;
                letter-spacing: ${px(4)}px;
                transition: opacity .15s;
              }
              .st-save-btn:disabled {
                opacity: .5;
                cursor: not-allowed;
              }
              .st-back-btn {
                position: absolute;
                bottom: ${px(40)}px;
                left: ${px(80)}px;
                right: ${px(80)}px;
                padding: ${px(30)}px;
                font-size: ${px(42)}px;
                font-weight: 900;
                border: ${px(3)}px solid #5e3a1a;
                border-radius: ${px(16)}px;
                background: rgba(240,223,212,.95);
                color: #5e3a1a;
                cursor: pointer;
                text-align: center;
                letter-spacing: ${px(4)}px;
              }
            `}</style>

            <div className="st-container">
              <h1 className="st-title">設定資料與載具</h1>

              {loading ? (
                <div style={{ textAlign: 'center', color: '#8b7355', fontSize: px(34) }}>載入中...</div>
              ) : (
                <div className="st-card">
                  <div className="st-field">
                    <label className="st-label">姓名</label>
                    <input
                      className="st-input"
                      type="text"
                      value={form.name}
                      onChange={handleChange('name')}
                      placeholder="請輸入姓名"
                    />
                  </div>

                  <div className="st-field">
                    <label className="st-label">信箱</label>
                    <input
                      className="st-input"
                      type="email"
                      value={form.email}
                      onChange={handleChange('email')}
                      placeholder="請輸入信箱"
                    />
                  </div>

                  <div className="st-field">
                    <label className="st-label">生日</label>
                    <input
                      className="st-input"
                      type="date"
                      value={form.birthday}
                      onChange={handleChange('birthday')}
                    />
                  </div>

                  <div className="st-field">
                    <label className="st-label">載具號碼</label>
                    <input
                      className="st-input"
                      type="text"
                      value={form.carrier}
                      onChange={handleChange('carrier')}
                      placeholder="/ABC1234"
                      maxLength={8}
                      style={{ textTransform: 'uppercase', letterSpacing: 3 }}
                    />
                    <div className="st-hint">手機條碼載具，格式為 / 開頭加 7 碼英數字</div>
                  </div>

                  {msg && (
                    <div className={`st-msg ${msg === '儲存成功！' ? 'st-msg-ok' : 'st-msg-err'}`}>
                      {msg}
                    </div>
                  )}

                  <button
                    className="st-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? '儲存中...' : '儲存'}
                  </button>
                </div>
              )}
            </div>

            <button
              className="st-back-btn"
              onClick={() => navigate('/')}
            >
              返回會員卡
            </button>
          </>
        )}
      />
    </FullBleed>
  );
}
